import ErrorMessage from "components/commons/ErrorMessage"
import Loading from "components/commons/Loading"
import { useEffect, useState } from "react"
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    ResponsiveContainer,
} from "recharts";
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useGetHealthStatsQuery } from "../events/api/healthEventApi"
import { COLORS } from "features/device/views/DeviceStats"
import { HealthEventCard } from "../events/views/HealthEventCard";
import { TopCriticalPatient } from "../events/views/TopCriticalPatient";
import { groupEventsByDate } from "utility/groupByDate";
import { formatCompactNumber } from "utility/numbers";


const HealthQuickDashboard = () => {


    const [t] = useTranslation(["global", "health"])
    const navigate = useNavigate()

    const [selectedDays, setSelectedDays] = useState(1);

    const {
        data,
        isError,
        isFetching,
        isSuccess,
        error,
        refetch,
    } = useGetHealthStatsQuery(selectedDays);

    // Refetch automatico su cambio filtro
    useEffect(() => {
        refetch(selectedDays)
    }, [selectedDays]);


    function getColor(type) {
        switch (type) {
            case "FALL_DETECT":
            case "NO_ACTIVITY": return "darkred"
            case "HIGH_HR":
            case "LOW_HR": return "#b33c31ff"
            case "SPO2": return "#2962d2ff"
            case "BIA": return "#72982cff"
            case "ECG": return "#1a803bff"
            default: return "#2c363c"
        }
    }

    function handleEventClick(event) {
        navigate("/dashboard/health/history",
            {
                state: {
                    userGUID: event.user.guid,
                    date: event.timestamp,
                }
            }
        )
    }

    function handleCriticalPatientClick(user) {
        navigate("/dashboard/health/history",
            {
                state: {
                    userGUID: user.guid
                }
            }
        )
    }


    const toPieData = (map) => {
        const total = Object.values(map).reduce((sum, val) => sum + val, 0);
        return Object.entries(map).map(([name, value]) => ({
            name,
            value,
            percentage: ((value / total) * 100).toFixed(1) + "%",
        }));
    };

    const renderPieChart = (data, title) => (
        <>
            <h6 className="text-center">{title}</h6>
            {data.length === 0 ? (
                <p className="text-center text-muted">{t("helper.no_data")}</p>
            ) : (
                <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            stroke="#000000"
                            strokeOpacity={0.2}
                            strokeWidth={5}
                            outerRadius={50}
                            innerRadius={30}
                            label={({ name, percentage }) => `${t(`health:entity.events.${name}`)} ${percentage}`}
                        >
                            {data.map((entry, index) => (
                                <Cell key={index} fill={getColor(entry.name)} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value}`} />
                    </PieChart>
                </ResponsiveContainer >
            )}
        </>
    );

    let groupedCriticalEvents
    let groupedNonCriticalEvents

    if (isSuccess) {
        groupedCriticalEvents = groupEventsByDate(data.latestCriticalEvents || []);
        groupedNonCriticalEvents = groupEventsByDate(data.latestNonCriticalEvents || []);
    }

    return (<div className={`container mt-4 ${isFetching ? "opacity-25" : ""}`}>
        <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0 text-muted"><i className="fa-solid fa-notes-medical"></i> {t(`health:menu.situation`)}</h4>
            {isFetching && <Loading />}
            <select
                className="form-select w-auto"
                value={selectedDays}
                onChange={(e) => setSelectedDays(Number(e.target.value))}
            >
                <option value={1}>{t("time.last_24_h")}</option>
                <option value={2}>{t("time.last_48_h")}</option>
                <option value={7}>{t("time.last_n_days", { days: 7 })}</option>
                <option value={30}>{t("time.last_n_days", { days: 30 })}</option>
                <option value={90}>{t("time.last_n_days", { days: 90 })}</option>
            </select>
        </div>

        {isError && <ErrorMessage error={error} />}

        {isSuccess && (
            <>
                <div className="row">
                    <div className="col">
                        <div className="card bg-dark text-bg-light">

                            <div className="card-body text-center" onClick={() => { navigate('/dashboard/health/dashboard') }}>
                                <h6 className="card-subtitle mb-2 text-muted">{t(`health:kpi.activePatients`)}</h6>
                                <div className="d-flex  align-items-center justify-content-center gap-2">
                                    <i className="fa-solid fa fa-user text-muted h5"></i>
                                    <h3 className="card-title"> {data.activePeople}</h3>
                                    <small> / {data.people}</small>
                                    <small className="text-muted">( {data.activePeopleRatio} %)</small>
                                </div>
                            </div>


                        </div>
                    </div>
                    <div className="col">
                        <div className="card bg-dark text-bg-light col">

                            <div className="card-body text-center" onClick={() => { navigate('/dashboard/health/profiles') }}>
                                <h6 className="card-subtitle mb-2 text-muted">{t("health:menu.profiles_ext")}</h6>
                                <h3 className="card-title "><i className="fa-solid fa fa-sliders text-muted"></i>  {data.profiles} </h3>
                            </div>
                        </div>
                    </div>
                    <div className="col">
                        <div className="card bg-dark text-bg-light col">

                            <div className="card-body text-center" onClick={() => { navigate('/dashboard/health/history') }}>
                                <h6 className="card-subtitle mb-2 text-muted">{t(`health:kpi.collected_data`)}</h6>
                                <div className="d-flex  align-items-center justify-content-center gap-2">
                                    <i className="fa-solid fa-chart-simple text-muted h5"></i>
                                    <h3 className="card-title"> {formatCompactNumber(data.data)}</h3>
                                    {
                                        data.dataImprovement != 0 &&
                                        <>{data.dataImprovement > 0 ? <small className="text-success" key="up"><i className="fa-solid fa-arrow-trend-up"></i> +{data.dataImprovement}%</small> : <small className="text-danger" key="down"><i className="fa-solid fa-arrow-trend-down"></i> {data.dataImprovement}%</small>}</>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row my-2">
                    <div className="col">
                        <div className="card bg-black bg-opacity-25 text-bg-light">
                            <div className="card-body ">
                                <div className="d-flex  align-items-center justify-content-center gap-2">
                                    <h6 className="text-danger"><i className="fa-solid fa-heart-circle-exclamation opacity-50"></i> {t(`health:kpi.criticalPatients`)}</h6>
                                    <h4>{data.criticalPatientsCount}</h4>
                                </div>
                                {
                                    data.criticalPatientsCount > 0 &&
                                    <><small className="text-muted">{t(`health:kpi.the_most_critical_ones`)}:</small>
                                        {
                                            data.topCriticalPatients.map((dto, i) => (
                                                <TopCriticalPatient index={i} eventsCount={dto.criticalEventCount} user={dto.user} key={dto.user.guid} onClick={() => handleCriticalPatientClick(dto.user)} />
                                            ))
                                        }
                                    </>
                                }

                            </div>
                        </div>


                    </div>
                    <div className="col">
                        <div className="card bg-black bg-opacity-25 text-bg-light">
                            <div className="card-body " >
                                <div className="d-flex  align-items-center justify-content-center gap-2" onClick={() => { navigate('/dashboard/health/events') }}>
                                    <h6 className="text-warning"><i className="fa-solid fa-triangle-exclamation opacity-50"></i> {t(`health:kpi.criticalEvents`)}</h6>
                                    <h4>{data.criticalEventsCount}</h4>
                                    {
                                        data.criticalEventsImprovement != 0 &&
                                        <>{data.criticalEventsImprovement > 0 ? <small className="text-danger" key="up"><i className="fa-solid fa-arrow-trend-up"></i> +{data.criticalEventsImprovement}%</small> : <small className="text-success" key="down"><i className="fa-solid fa-arrow-trend-down"></i> {data.criticalEventsImprovement}%</small>}</>
                                    }
                                </div>

                                {Object.entries(groupedCriticalEvents).map(([date, events]) => (
                                    <div key={"c" + date} className="mt-2">
                                        <div className="text-capitalize text-muted mb-1">{date}</div>
                                        {events.map((event) => (
                                            <HealthEventCard key={event.timestamp + event.user.guid} event={event} onClick={() => handleEventClick(event)} />
                                        ))}
                                    </div>
                                ))}
                                {
                                    data.latestCriticalEvents.length < data.criticalEventsCount &&
                                    <button className="btn btn-dark w-100" onClick={() => navigate('/dashboard/health/events')}> + <i className="fa-solid fa-layer-group"></i> {data.criticalEventsCount - data.latestCriticalEvents.length}</button>
                                }
                            </div>
                        </div>

                    </div>
                    <div className="col">
                        <div className="card bg-black bg-opacity-25 text-bg-light ">
                            <div className="card-body ">
                                <div className="d-flex  align-items-center justify-content-center gap-2" onClick={() => { navigate('/dashboard/health/events') }}>
                                    <h6 className="text-primary"><i className="fa-solid fa-flask opacity-50"></i> {t(`health:kpi.measurements`)}</h6>
                                    <h4>{data.nonCriticalEventsCount}</h4>
                                </div>


                                {Object.entries(groupedNonCriticalEvents).map(([date, events]) => (
                                    <div key={"m" + date} className={"mt-2"}>
                                        <div className="text-capitalize text-muted mb-1">{date}</div>
                                        {events.map((event) => (
                                            <HealthEventCard key={event.timestamp + event.user.guid} event={event} onClick={() => handleEventClick(event)} />
                                        ))}
                                    </div>
                                ))}



                                {
                                    data.latestNonCriticalEvents.length < data.nonCriticalEventsCount &&
                                    <button className="btn btn-dark w-100" onClick={() => navigate('/dashboard/health/events')}>+ <i className="fa-solid fa-layer-group"> </i> {data.nonCriticalEventsCount - data.latestNonCriticalEvents.length}</button>
                                }
                            </div>
                        </div>
                    </div>
                    {/* <div className="col-md-6">
                        <div className="row mt-3">
                            {renderPieChart(toPieData(data.criticalEventStats), "Criticità per tipo")}
                        </div>
                    </div> */}
                </div>
            </>
        )
        }
    </div >)
}

export default HealthQuickDashboard